import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, Button, InputNumber, Skeleton, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import {
  getAverageTicketBuckets,
  replaceAverageTicketBuckets,
} from "../../api/Services";

import styles from "./RewardsTicketBucketsEditor.module.scss";

type RewardTypeOption = {
  id: number;
  name: string;
  excluded: boolean;
};

type TypeValueDraft = {
  minValue: number | null;
  maxValue: number | null;
};

type BucketDraft = {
  key: string;
  min: number | null;
  max: number | null;
  typeValues: Record<number, TypeValueDraft>;
};

type BucketsResponse = {
  rewardTypes?: RewardTypeOption[];
  buckets?: Array<{
    min: number;
    max: number;
    typeValues?: Array<{
      rewardTypeId: number;
      minValue: number;
      maxValue: number;
    }>;
  }>;
};

let draftKey = 0;

const toNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const nextKey = () => {
  draftKey += 1;
  return `bucket-${draftKey}`;
};

const emptyTypeValues = (types: RewardTypeOption[]): Record<number, TypeValueDraft> => {
  const typeValues: Record<number, TypeValueDraft> = {};
  types.forEach((type) => {
    typeValues[type.id] = { minValue: null, maxValue: null };
  });
  return typeValues;
};

const toDrafts = (response: BucketsResponse, types: RewardTypeOption[]): BucketDraft[] =>
  (response.buckets ?? []).map((bucket) => {
    const typeValues = emptyTypeValues(types);
    (bucket.typeValues ?? []).forEach((typeValue) => {
      typeValues[typeValue.rewardTypeId] = {
        minValue: typeValue.minValue,
        maxValue: typeValue.maxValue,
      };
    });
    return {
      key: nextKey(),
      min: bucket.min,
      max: bucket.max,
      typeValues,
    };
  });

const apiErrorMessage = (error: any, fallback: string) => {
  const apiError = error?.response?.data?.apierror;
  return (
    apiError?.message_ES ??
    apiError?.message ??
    error?.response?.data?.message_ES ??
    error?.response?.data?.message ??
    fallback
  );
};

const RewardsTicketBucketsEditor = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rewardTypes, setRewardTypes] = useState<RewardTypeOption[]>([]);
  const [buckets, setBuckets] = useState<BucketDraft[]>([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = (await getAverageTicketBuckets()) as BucketsResponse;
        if (!active) return;
        const types = response.rewardTypes ?? [];
        setRewardTypes(types);
        setBuckets(toDrafts(response, types));
      } catch (error) {
        if (!active) return;
        message.error(apiErrorMessage(error, t("Common-ErrorMessage")));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [t]);

  const updateBucket = (key: string, patch: Partial<BucketDraft>) => {
    setBuckets((current) =>
      current.map((bucket) => (bucket.key === key ? { ...bucket, ...patch } : bucket))
    );
  };

  const updateTypeValue = (
    key: string,
    rewardTypeId: number,
    field: keyof TypeValueDraft,
    value: number | null
  ) => {
    setBuckets((current) =>
      current.map((bucket) => {
        if (bucket.key !== key) return bucket;
        const existing = bucket.typeValues[rewardTypeId] ?? {
          minValue: null,
          maxValue: null,
        };
        return {
          ...bucket,
          typeValues: {
            ...bucket.typeValues,
            [rewardTypeId]: { ...existing, [field]: value },
          },
        };
      })
    );
  };

  const addBucket = () => {
    const previous = buckets[buckets.length - 1];
    setBuckets((current) => [
      ...current,
      {
        key: nextKey(),
        min: previous?.max ?? null,
        max: null,
        typeValues: emptyTypeValues(rewardTypes),
      },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = (await replaceAverageTicketBuckets({
        buckets: buckets.map((bucket) => ({
          min: bucket.min,
          max: bucket.max,
          typeValues: rewardTypes.flatMap((type) => {
            const values = bucket.typeValues[type.id] ?? {
              minValue: null,
              maxValue: null,
            };
            if (type.excluded && values.minValue == null && values.maxValue == null) {
              return [];
            }
            return [
              {
                rewardTypeId: type.id,
                minValue: values.minValue,
                maxValue: values.maxValue,
              },
            ];
          }),
        })),
      })) as BucketsResponse;
      const types = response.rewardTypes ?? rewardTypes;
      setRewardTypes(types);
      setBuckets(toDrafts(response, types));
      message.success(t("RewardsEngineParam-BucketsSaved"));
    } catch (error) {
      message.error(apiErrorMessage(error, t("Common-ErrorMessage")));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.editor}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  return (
    <div className={styles.editor}>
      <div className={styles.help}>{t("RewardsEngineParam-BucketsTitle")}</div>
      <div className={styles.help}>{t("RewardsEngineParam-BucketsHelp")}</div>
      {buckets.length === 0 && (
        <Alert
          className={styles.help}
          type="warning"
          showIcon
          message={t("RewardsEngineParam-BucketsEmpty")}
        />
      )}
      {rewardTypes.length === 0 && (
        <Alert
          className={styles.help}
          type="info"
          showIcon
          message={t("RewardsEngineParam-NoRewardTypes")}
        />
      )}
      {buckets.map((bucket, index) => (
        <div key={bucket.key} className={styles.bucket}>
          <div className={styles.rangeRow}>
            <div className={styles.field}>
              <div>{t("RewardsEngineParam-BucketMinimum")}</div>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={bucket.min ?? undefined}
                onChange={(value) =>
                  updateBucket(bucket.key, {
                    min: toNumber(value),
                  })
                }
              />
            </div>
            <div className={styles.field}>
              <div>{t("RewardsEngineParam-BucketMaximum")}</div>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={bucket.max ?? undefined}
                onChange={(value) =>
                  updateBucket(bucket.key, {
                    max: toNumber(value),
                  })
                }
              />
            </div>
            <Button
              danger
              icon={<DeleteOutlined />}
              aria-label={`${t("RewardsEngineParam-RemoveBucket")} ${index + 1}`}
              onClick={() =>
                setBuckets((current) => current.filter((item) => item.key !== bucket.key))
              }
            />
          </div>
          {rewardTypes.map((type) => {
            const values = bucket.typeValues[type.id] ?? {
              minValue: null,
              maxValue: null,
            };
            return (
              <div key={type.id} className={styles.typeRow}>
                <div className={styles.typeName}>
                  {type.name}
                  {type.excluded && (
                    <span className={styles.optional}>
                      {t("RewardsEngineParam-OptionalType")}
                    </span>
                  )}
                </div>
                <div className={styles.field}>
                  <div>{t("RewardsEngineParam-TypeMinimum")}</div>
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={values.minValue ?? undefined}
                    onChange={(value) =>
                      updateTypeValue(bucket.key, type.id, "minValue", toNumber(value))
                    }
                  />
                </div>
                <div className={styles.field}>
                  <div>{t("RewardsEngineParam-TypeMaximum")}</div>
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={values.maxValue ?? undefined}
                    onChange={(value) =>
                      updateTypeValue(bucket.key, type.id, "maxValue", toNumber(value))
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div className={styles.actions}>
        <Button icon={<PlusOutlined />} onClick={addBucket}>
          {t("RewardsEngineParam-AddBucket")}
        </Button>
        <Button type="primary" loading={saving} onClick={save}>
          {t("RewardsEngineParam-SaveBuckets")}
        </Button>
      </div>
    </div>
  );
};

export default RewardsTicketBucketsEditor;
